/**
 * Australian Vehicle Stamp Duty Rules
 * 
 * This file contains the duty rates, thresholds, and exemptions for each Australian state and territory.
 * Data is accurate as of April 2025 but should be regularly updated as duty rates and policies change.
 */

export interface DutyTier {
  threshold: number;
  rateBelow: number;
  rateAbove: number;
}

export interface DutyFlatRate {
  rate: number;
}

export interface DutyProgressiveTier {
  upTo: number;
  rate: number;
}

export interface DutyExemptions {
  EVExemption?: boolean;
  EVExemptionEndDate?: string;
  EVDiscountPercentage?: number;
  EVDiscountAmount?: number;
  pensioner?: boolean;
  disability?: boolean;
  familyTransfer?: boolean;
  primaryProducer?: boolean;
}

export interface StateRules {
  passenger: DutyTier[] | DutyFlatRate | DutyProgressiveTier[];
  commercial?: DutyTier[] | DutyFlatRate;
  motorcycle?: DutyTier[] | DutyFlatRate;
  heavyVehicle?: DutyTier[] | DutyFlatRate;
  EVRules?: {
    isExempt: boolean;
    discountPercentage?: number;
    discountAmount?: number;
    endDate?: string;
  };
  cylinderRules?: {
    [key: string]: {
      belowThreshold: number;
      aboveThreshold: number;
      threshold: number;
    };
  };
  CO2Rules?: {
    [category: string]: {
      rate: number;
      range?: [number, number]; // g/km range
    };
  };
  exemptions: DutyExemptions;
  minimumDuty?: number;
}

export interface StampDutyRules {
  [state: string]: StateRules;
}

/**
 * Australian Vehicle Stamp Duty Rules Data
 * 
 * This data structure contains all rules needed to calculate vehicle stamp duty
 * across all Australian states and territories.
 */
export const STAMP_DUTY_RULES: StampDutyRules = {
  "NSW": {
    passenger: [
      { threshold: 45000, rateBelow: 3, rateAbove: 5 }
    ],
    commercial: { rate: 3 },
    motorcycle: { rate: 3 },
    exemptions: {
      EVExemption: true,
      EVExemptionEndDate: "2027-06-30",
      pensioner: true,
      disability: true,
      familyTransfer: true
    },
    minimumDuty: 10
  },
  "VIC": {
    passenger: [
      { upTo: 80567, rate: 4.2 },
      { upTo: 100000, rate: 5.2 },
      { upTo: 150000, rate: 7.0 },
      { upTo: Infinity, rate: 9.0 }
    ],
    commercial: { rate: 2.7 },
    motorcycle: { rate: 2.7 },
    EVRules: {
      isExempt: true,
      endDate: "2025-06-30" 
    },
    exemptions: {
      pensioner: true,
      disability: true,
      familyTransfer: true,
      primaryProducer: true
    },
    minimumDuty: 10
  },
  "QLD": {
    passenger: { rate: 3 }, // Default rate, actual calculation uses cylinderRules
    commercial: { rate: 3 },
    motorcycle: { rate: 3 },
    cylinderRules: {
      "EV_HYBRID": {
        belowThreshold: 2,
        aboveThreshold: 4,
        threshold: 100000
      },
      "1_4": {
        belowThreshold: 3,
        aboveThreshold: 5,
        threshold: 100000
      },
      "5_6": {
        belowThreshold: 3.5,
        aboveThreshold: 5.5,
        threshold: 100000
      },
      "7plus": {
        belowThreshold: 4,
        aboveThreshold: 6,
        threshold: 100000
      }
    },
    exemptions: {
      disability: true,
      familyTransfer: true
    },
    minimumDuty: 25
  },
  "WA": {
    passenger: [
      { upTo: 25000, rate: 2.75 },
      { upTo: 50000, rate: 2.75 },
      { upTo: Infinity, rate: 6.5 }
    ],
    commercial: { rate: 3 },
    motorcycle: { rate: 2.75 },
    EVRules: {
      isExempt: false,
      discountAmount: 3000
    },
    exemptions: {
      disability: true,
      familyTransfer: true
    },
    minimumDuty: 0
  },
  "SA": {
    passenger: [
      { upTo: 3000, rate: 1 },
      { upTo: 60000, rate: 4 },
      { upTo: Infinity, rate: 5 }
    ],
    commercial: { rate: 3 },
    motorcycle: { rate: 3 },
    EVRules: {
      isExempt: true,
      endDate: "2027-06-30"
    },
    exemptions: {
      disability: true,
      familyTransfer: true
    },
    minimumDuty: 10
  },
  "TAS": {
    passenger: { rate: 3 },
    commercial: { rate: 3 },
    motorcycle: { rate: 3 },
    EVRules: {
      isExempt: true,
      endDate: "2023-06-30" // Already expired
    },
    exemptions: {
      disability: true,
      familyTransfer: true
    },
    minimumDuty: 20
  },
  "ACT": {
    passenger: { rate: 0 }, // Default rate, actual calculation uses CO2Rules
    commercial: { rate: 3 },
    motorcycle: { rate: 3 },
    CO2Rules: {
      "A": { rate: 0, range: [0, 120] }, // 0 g/km - 120 g/km
      "B": { rate: 2, range: [121, 175] }, // 121 g/km - 175 g/km
      "C": { rate: 3, range: [176, 225] }, // 176 g/km - 225 g/km
      "D": { rate: 4, range: [226, Infinity] } // 226+ g/km
    },
    EVRules: {
      isExempt: true,
      endDate: "2024-06-30"
    },
    exemptions: {
      disability: true,
      familyTransfer: true
    },
    minimumDuty: 0
  },
  "NT": {
    passenger: { rate: 3 },
    commercial: { rate: 3 },
    motorcycle: { rate: 3 },
    EVRules: {
      isExempt: false,
      discountAmount: 1500
    },
    exemptions: {
      disability: true,
      familyTransfer: true,
      primaryProducer: true
    },
    minimumDuty: 5
  }
};

// Helper function to check if a date is in the past (for EV exemption end dates)
export function isDatePassed(dateString: string | undefined): boolean {
  if (!dateString) return false;
  
  const endDate = new Date(dateString);
  const today = new Date();
  
  return endDate < today;
}

// Vehicle type options
export const VEHICLE_TYPES = [
  { value: "passenger", label: "Passenger Vehicle" },
  { value: "commercial", label: "Commercial Vehicle" },
  { value: "motorcycle", label: "Motorcycle" },
  { value: "heavyVehicle", label: "Heavy Vehicle" }
];

// Cylinder options for QLD
export const CYLINDER_OPTIONS = [
  { value: "EV_HYBRID", label: "Electric Vehicle or Hybrid" },
  { value: "1_4", label: "1-4 Cylinders" },
  { value: "5_6", label: "5-6 Cylinders" },
  { value: "7plus", label: "7+ Cylinders" }
];

// CO2 emission options for ACT
export const CO2_OPTIONS = [
  { value: "A", label: "Category A (0-120 g/km)" },
  { value: "B", label: "Category B (121-175 g/km)" },
  { value: "C", label: "Category C (176-225 g/km)" },
  { value: "D", label: "Category D (226+ g/km)" }
];

// State/Territory options
export const STATE_OPTIONS = [
  { value: "NSW", label: "New South Wales" },
  { value: "VIC", label: "Victoria" },
  { value: "QLD", label: "Queensland" },
  { value: "WA", label: "Western Australia" },
  { value: "SA", label: "South Australia" },
  { value: "TAS", label: "Tasmania" },
  { value: "ACT", label: "Australian Capital Territory" },
  { value: "NT", label: "Northern Territory" }
];

// Concession types
export const CONCESSION_OPTIONS = [
  { value: "none", label: "No Concessions" },
  { value: "EVs", label: "Electric Vehicle" },
  { value: "pensioner", label: "Pensioner" },
  { value: "disability", label: "Disability" },
  { value: "familyTransfer", label: "Family Transfer" },
  { value: "primaryProducer", label: "Primary Producer" }
]; 